import React from 'react';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';

// ─── Types ──────────────────────────────────────────────────────────

export interface ThemeFormValues {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  infoColor: string;
  grayColor: string;
  headingFontFamily: string;
  bodyFontFamily: string;
  headingFontSize: string;
  bodyFontSize: string;
  logoUrl: string;
  logoMarkUrl: string;
  faviconUrl: string;
  borderRadius: string;
  customCss: string;
  darkModeEnabled: boolean;
  dateFormat: string;
  timeFormat: string;
  weekStart: number | string;
  showCalendarWeeks: boolean;
  thousandsSeparator: string;
  decimalSeparator: string;
  currencySymbol: string;
  currencyFormat: string;
}

export const EMPTY_THEME_FORM: ThemeFormValues = {
  primaryColor: '',
  secondaryColor: '',
  accentColor: '',
  successColor: '',
  warningColor: '',
  errorColor: '',
  infoColor: '',
  grayColor: '',
  headingFontFamily: '',
  bodyFontFamily: '',
  headingFontSize: '',
  bodyFontSize: '',
  logoUrl: '',
  logoMarkUrl: '',
  faviconUrl: '',
  borderRadius: '',
  customCss: '',
  darkModeEnabled: true,
  dateFormat: '',
  timeFormat: '',
  weekStart: '',
  showCalendarWeeks: true,
  thousandsSeparator: '',
  decimalSeparator: '',
  currencySymbol: '',
  currencyFormat: '',
};

const CURATED_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Nunito',
  'Montserrat', 'Raleway', 'Source Sans 3', 'Work Sans', 'DM Sans',
  'Plus Jakarta Sans', 'IBM Plex Sans', 'Outfit', 'Space Grotesk',
];

const HEADING_FONT_SIZE_OPTIONS = [
  { value: '', label: 'Default (32px)' },
  { value: '1.5rem', label: '24px' },
  { value: '1.75rem', label: '28px' },
  { value: '2rem', label: '32px' },
  { value: '2.25rem', label: '36px' },
  { value: '2.5rem', label: '40px' },
];

const BODY_FONT_SIZE_OPTIONS = [
  { value: '', label: 'Default (16px)' },
  { value: '0.75rem', label: '12px' },
  { value: '0.875rem', label: '14px' },
  { value: '1rem', label: '16px' },
  { value: '1.125rem', label: '18px' },
  { value: '1.25rem', label: '20px' },
];

const DATE_FORMAT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'HH:mm', label: 'HH:mm (24h)' },
  { value: 'hh:mm A', label: 'hh:mm A (12h)' },
  { value: 'HH:mm:ss', label: 'HH:mm:ss' },
];

const WEEK_START_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 1, label: 'Monday' },
  { value: 0, label: 'Sunday' },
];

const THOUSANDS_SEPARATOR_OPTIONS = [
  { value: '', label: 'Default' },
  { value: '.', label: '. (period)' },
  { value: ',', label: ', (comma)' },
  { value: "'", label: "' (apostrophe)" },
  { value: ' ', label: '(space)' },
];

const DECIMAL_SEPARATOR_OPTIONS = [
  { value: '', label: 'Default' },
  { value: ',', label: ', (comma)' },
  { value: '.', label: '. (period)' },
];

const CURRENCY_FORMAT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'value_first', label: 'XX,YY \u20ac (value first)' },
  { value: 'symbol_first', label: '\u20ac XX.YY (symbol first)' },
];

const BORDER_RADIUS_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'none', label: 'None (sharp)' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' },
];

interface ThemeEditorPanelProps {
  form: ThemeFormValues;
  onChange: <K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => void;
  onSave: () => void;
  onReset: () => void;
  saving?: boolean;
  resetting?: boolean;
  error?: string | null;
  saved?: boolean;
  /** Platform defaults (shown as placeholders in org mode) */
  platformDefaults?: Partial<ThemeFormValues> | null;
  mode: 'platform' | 'organization';
}

// ─── Component ──────────────────────────────────────────────────────

export function ThemeEditorPanel({
  form, onChange, onSave, onReset,
  saving, resetting, error, saved,
  platformDefaults, mode,
}: ThemeEditorPanelProps) {
  return (
    <div className="space-y-6 p-4 overflow-y-auto" data-no-translate>
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {mode === 'platform' ? 'Platform Design Template' : 'Organization Theme'}
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          {mode === 'platform'
            ? 'Set the default design for all organizations. Changes preview live on the right.'
            : 'Override platform defaults for your organization. Empty fields inherit from platform.'}
        </p>
      </div>

      {saved && (
        <div className="bg-success-50 text-success-700 px-3 py-2 rounded-lg text-sm">
          Theme saved successfully.
        </div>
      )}
      {error && (
        <div className="bg-error-50 text-error-700 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* ── Brand Colors ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Brand Colors</h3>
        <div className="grid grid-cols-1 gap-3">
          <ColorPicker
            label="Primary"
            value={form.primaryColor}
            onChange={(v) => onChange('primaryColor', v)}
            placeholder={platformDefaults?.primaryColor}
            hint="CTAs, buttons, links"
            defaultColor="#0d8080"
          />
          <ColorPicker
            label="Secondary"
            value={form.secondaryColor}
            onChange={(v) => onChange('secondaryColor', v)}
            placeholder={platformDefaults?.secondaryColor}
            hint="Secondary actions"
            defaultColor="#f59e0b"
          />
          <ColorPicker
            label="Accent"
            value={form.accentColor}
            onChange={(v) => onChange('accentColor', v)}
            placeholder={platformDefaults?.accentColor}
            hint="Highlights, special elements"
            defaultColor="#f437a8"
          />
        </div>
      </section>

      {/* ── Status Colors ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Status Colors</h3>
        <div className="grid grid-cols-2 gap-3">
          <ColorPicker
            label="Success"
            value={form.successColor}
            onChange={(v) => onChange('successColor', v)}
            placeholder={platformDefaults?.successColor}
            defaultColor="#22c55e"
          />
          <ColorPicker
            label="Warning"
            value={form.warningColor}
            onChange={(v) => onChange('warningColor', v)}
            placeholder={platformDefaults?.warningColor}
            defaultColor="#f59e0b"
          />
          <ColorPicker
            label="Error"
            value={form.errorColor}
            onChange={(v) => onChange('errorColor', v)}
            placeholder={platformDefaults?.errorColor}
            defaultColor="#ef4444"
          />
          <ColorPicker
            label="Info"
            value={form.infoColor}
            onChange={(v) => onChange('infoColor', v)}
            placeholder={platformDefaults?.infoColor}
            defaultColor="#3b82f6"
          />
        </div>
      </section>

      {/* ── Gray Base ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Gray Palette</h3>
        <ColorPicker
          label="Gray Base"
          value={form.grayColor}
          onChange={(v) => onChange('grayColor', v)}
          placeholder={platformDefaults?.grayColor}
          hint="Borders, backgrounds, muted text"
          defaultColor="#e2e4e8"
        />
      </section>

      {/* ── Typography ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Typography</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Heading Font</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.headingFontFamily}
              onChange={(e) => onChange('headingFontFamily', e.target.value)}
            >
              <option value="">{platformDefaults?.headingFontFamily ? `Inherited (${platformDefaults.headingFontFamily})` : 'Default (Inter)'}</option>
              {CURATED_FONTS.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Body Font</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.bodyFontFamily}
              onChange={(e) => onChange('bodyFontFamily', e.target.value)}
            >
              <option value="">{platformDefaults?.bodyFontFamily ? `Inherited (${platformDefaults.bodyFontFamily})` : 'Default (Inter)'}</option>
              {CURATED_FONTS.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Heading Font Size</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.headingFontSize}
              onChange={(e) => onChange('headingFontSize', e.target.value)}
            >
              {HEADING_FONT_SIZE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Body Font Size</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.bodyFontSize}
              onChange={(e) => onChange('bodyFontSize', e.target.value)}
            >
              {BODY_FONT_SIZE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ── Border Radius ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Border Radius</h3>
        <div className="flex flex-wrap gap-2">
          {BORDER_RADIUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('borderRadius', opt.value)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                form.borderRadius === opt.value
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-background text-foreground border-border hover:border-primary-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Branding Assets ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Branding</h3>
        <div className="space-y-3">
          <Input
            label="Logo URL"
            type="url"
            value={form.logoUrl}
            onChange={(e) => onChange('logoUrl', e.target.value)}
            placeholder={platformDefaults?.logoUrl || 'https://example.com/logo.png'}
          />
          <Input
            label="Logo Mark URL"
            type="url"
            value={form.logoMarkUrl}
            onChange={(e) => onChange('logoMarkUrl', e.target.value)}
            placeholder={platformDefaults?.logoMarkUrl || 'https://example.com/mark.png'}
          />
          <Input
            label="Favicon URL"
            type="url"
            value={form.faviconUrl}
            onChange={(e) => onChange('faviconUrl', e.target.value)}
            placeholder={platformDefaults?.faviconUrl || 'https://example.com/favicon.ico'}
          />
        </div>
      </section>

      {/* ── Display Settings ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Display</h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.darkModeEnabled}
            onChange={(e) => onChange('darkModeEnabled', e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm text-foreground">Allow dark mode</span>
        </label>
      </section>

      {/* ── Locale & Formatting ── */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Locale & Formatting</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Date Format</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.dateFormat}
              onChange={(e) => onChange('dateFormat', e.target.value)}
            >
              {DATE_FORMAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Time Format</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.timeFormat}
              onChange={(e) => onChange('timeFormat', e.target.value)}
            >
              {TIME_FORMAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Week Start</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.weekStart}
              onChange={(e) => {
                const v = e.target.value;
                onChange('weekStart', v === '' ? '' : Number(v));
              }}
            >
              {WEEK_START_OPTIONS.map(opt => (
                <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.showCalendarWeeks}
              onChange={(e) => onChange('showCalendarWeeks', e.target.checked)}
              className="rounded border-border"
            />
            <span className="text-sm text-foreground">Show calendar weeks</span>
          </label>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Thousands Separator</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.thousandsSeparator}
              onChange={(e) => onChange('thousandsSeparator', e.target.value)}
            >
              {THOUSANDS_SEPARATOR_OPTIONS.map(opt => (
                <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Decimal Separator</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.decimalSeparator}
              onChange={(e) => onChange('decimalSeparator', e.target.value)}
            >
              {DECIMAL_SEPARATOR_OPTIONS.map(opt => (
                <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Currency Symbol</label>
            <input
              type="text"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.currencySymbol}
              onChange={(e) => onChange('currencySymbol', e.target.value)}
              placeholder={'\u20ac'}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Currency Format</label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.currencyFormat}
              onChange={(e) => onChange('currencyFormat', e.target.value)}
            >
              {CURRENCY_FORMAT_OPTIONS.map(opt => (
                <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>


      {/* ── Actions ── */}
      <div className="flex items-center gap-3 pt-4 border-t border-border">
        <Button type="button" variant="primary" onClick={onSave} loading={saving}>
          Save
        </Button>
        <Button type="button" variant="outline" onClick={onReset} loading={resetting}>
          Reset
        </Button>
      </div>
    </div>
  );
}

// ─── Color Picker ───────────────────────────────────────────────────

function ColorPicker({ label, value, onChange, hint, placeholder, defaultColor }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  placeholder?: string;
  defaultColor?: string;
}) {
  const fallback = defaultColor || '#6366f1';
  return (
    <div>
      <label className="block text-xs font-medium text-foreground mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || placeholder || fallback}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border border-border cursor-pointer p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          placeholder={placeholder || fallback}
          className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-mono"
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
